import { Dialog, DialogContent, DialogTitle } from '@/components/Dialog';
import { Divider } from '@/components/Divider';
import { Text as TextInput } from '@/components/forms/Text';
import Text from '@/components/Text';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { DataStreamBaseTileConfig, ShapeName } from '@squaredup/data-streams';
import TooltipContainerContext from 'contexts/TooltipContainerContext';
import { Dispatch, FC, useRef, useState } from 'react';
import { ShapeConfigForm } from './components/ShapeConfigForm';
import { ShapeDropdown } from './components/ShapeDropdown';
import { DataStreamMetadataAction } from './MetadataEditorReducer';
import {
    CloneStateColumn,
    ComparisonStateColumn,
    ComputedStateColumn,
    DataStreamMetadataState,
    StateColumn
} from './MetadataEditorState';
import { MetadataTableRow } from './MetadataEditorTable';

export const RowMetadataModal: FC<{
    row: MetadataTableRow;
    columns: (StateColumn | CloneStateColumn | ComparisonStateColumn | ComputedStateColumn)[];
    state: DataStreamMetadataState;
    config: DataStreamBaseTileConfig;
    dispatch: Dispatch<DataStreamMetadataAction>;
    onClose: () => void;
}> = ({ row, columns, state, config, onClose, dispatch }) => {
    const [columnDisplayName, setColumnDisplayName] = useState(row.displayName.value);
    const [columnShape, setColumnShape] = useState(row.shape as ShapeName);

    const dialogBody = useRef<HTMLDivElement>(null);

    // Track format/value expression when switching column shape
    const defaultExpressions = useRef<Record<string, string | undefined>>({});

    return (
        <Dialog open onOpenChange={() => onClose()}>
            <VisuallyHidden asChild>
                <DialogTitle>Configure column: ${row.displayName.value}</DialogTitle>
            </VisuallyHidden>

            <DialogContent
                ref={dialogBody}
                aria-describedby={undefined}
                data-testid='metadata-editor-modal'
                className='z-30 overflow-y-auto overflow-x-hidden max-h-[min(820px,95vh)] flex flex-col pt-4 absolute top-1/2 left-1/2 h-full -translate-x-1/2 max-w-3xl w-full -translate-y-1/2 border-2 bg-backgroundSecondary border-modalOutline shadow-lg scrollbar-thin scrollbar-track-transparent scrollbar-thumb-statusUnknownPrimary'
            >
                <TooltipContainerContext.Provider value={dialogBody.current}>
                    <div className='flex flex-col flex-1'>
                        <div className='px-8'>
                            <Text.H2 className='truncate text-textPrimary'>
                                Configure column: {row.displayName.value}
                            </Text.H2>
                            <Text.Body className='mt-4 text-textSecondary'>
                                Customize how this column is displayed. Custom formatting allows the use of JavaScript
                                expressions for complete control.{' '}
                                <a
                                    href='https://docs.squaredup.com/advanced-features/custom-formatting'
                                    className='text-textLink'
                                    target='_blank'
                                    rel='noopener noreferrer'
                                >
                                    Learn more
                                </a>
                            </Text.Body>
                        </div>

                        <div className='px-8'>
                            <div className='relative grid grid-cols-2 gap-12 mt-6'>
                                <div>
                                    <label className='block mb-2 font-medium' htmlFor='columnName'>
                                        Name
                                    </label>
                                    <TextInput
                                        value={columnDisplayName}
                                        onChange={(e) => setColumnDisplayName(e.target.value)}
                                        id='columnName'
                                    />
                                </div>

                                <div>
                                    <label className='block mb-2 font-medium' htmlFor='shape'>
                                        Type
                                    </label>
                                    <ShapeDropdown
                                        columns={columns}
                                        row={row}
                                        columnShape={columnShape}
                                        onChange={setColumnShape}
                                    />
                                </div>
                            </div>
                            
                            <Divider className='w-full h-px my-6 bg-dividerPrimary' />
                        </div>


                        <ShapeConfigForm
                            key={columnShape}
                            row={row}
                            columns={columns}
                            columnShape={columnShape}
                            columnDisplayName={columnDisplayName}
                            stateCopy={{ ...state }}
                            config={config}
                            defaultExpressions={defaultExpressions}
                            onClose={onClose}
                            dispatch={dispatch}
                        />
                    </div>
                </TooltipContainerContext.Provider>
            </DialogContent>
        </Dialog>
    );
};
